/**
 * Running / paused screen (BUILD-SPEC.md §7). One tree for both the meeting
 * side panel and the meeting stage — `size="stage"` just suppresses the
 * controls block, it never re-implements the layout.
 *
 * All maths comes from src/engine (elapsedFor/remainingFor/limitFor/
 * colourState/totalElapsed/budgetSeconds) driven by a `now` prop supplied
 * by the caller (App.tsx) from `controller.now()` — this component never
 * touches a clock itself.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  ProgressBar,
  Switch,
  Text,
  tokens,
} from "@fluentui/react-components";
import { budgetSeconds, colourState, limitFor, remainingFor, totalElapsed, type Session } from "../engine";
import type { AddPersonCandidate } from "./AddPerson";
import { AddPerson } from "./AddPerson";
import { Countdown } from "./Countdown";
import { formatDuration } from "./format";
import { Queue } from "./Queue";
import type { ScreenSize } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  speaker: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightBold,
    margin: 0,
  },
  turnLabel: {
    color: tokens.colorNeutralForeground2,
    margin: 0,
  },
  total: {
    color: tokens.colorNeutralForeground2,
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalS,
  },
  sr: {
    position: "absolute",
    width: "1px",
    height: "1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
  },
});

export interface RunningProps {
  session: Session;
  now: number;
  size: ScreenSize;
  shared: boolean;
  soundEnabled: boolean;
  onToggleSound: (on: boolean) => void;
  rosterCandidates: AddPersonCandidate[];
  onNext: (expectedIdx: number) => void;
  onPause: () => void;
  onResume: () => void;
  onExtend: (seconds: number) => void;
  onAddManual: (name: string) => void;
  onAddInvited: (userId: string, name: string) => void;
  onEnd: () => void;
  /** Minimal sink so Running stays testable without a real AudioContext —
   * App.tsx passes the real src/ui/sound.ts singleton; tests can pass a spy. */
  sound?: {
    handOff(): void;
    checkOvertime(remainingSeconds: number, graceSeconds: number): void;
  };
}

export function Running(props: RunningProps) {
  const {
    session,
    now,
    size,
    shared,
    soundEnabled,
    onToggleSound,
    rosterCandidates,
    onNext,
    onPause,
    onResume,
    onExtend,
    onAddManual,
    onAddInvited,
    onEnd,
    sound,
  } = props;
  const styles = useStyles();
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);

  const idx = session.idx;
  const current = session.queue[idx];
  const remaining = current ? remainingFor(session, idx, now) : 0;
  const limit = current ? limitFor(session, idx) : session.limitSeconds;
  const colour = current ? colourState(session, idx, now) : "normal";
  const progress = limit > 0 ? Math.max(0, Math.min(1, remaining / limit)) : 0;
  const total = totalElapsed(session, now);
  const budget = budgetSeconds(session);

  const wasOvertime = useRef(false);
  const lastAnnouncedIdx = useRef<number | null>(null);
  const [politeMessage, setPoliteMessage] = useState("");
  const [assertiveMessage, setAssertiveMessage] = useState("");

  // Hand-off announcement + sound cue, fired exactly once per turn change.
  useEffect(() => {
    if (lastAnnouncedIdx.current === idx || !current) return;
    lastAnnouncedIdx.current = idx;
    wasOvertime.current = false;
    setPoliteMessage(`Now speaking: ${current.displayName}, ${formatDuration(limitFor(session, idx))}`);
    sound?.handOff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Time-up ARIA announcement, fired exactly once when this turn first
  // crosses zero. Never re-fires while overtime keeps counting up, and
  // never fires on ordinary ticks (BUILD-SPEC.md §7: "never announce ticks").
  useEffect(() => {
    if (!current) return;
    const isOver = remaining < 0;
    if (isOver && !wasOvertime.current) {
      wasOvertime.current = true;
      setAssertiveMessage(`Time's up for ${current.displayName}`);
    }
    if (!isOver) {
      wasOvertime.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining < 0]);

  // The limit/grace tones are gated by sound.ts's own once-per-turn stage
  // counter, so it's safe to call this on every render while running — it
  // only actually plays a tone the instant `remaining` first crosses each
  // threshold.
  useEffect(() => {
    if (!current) return;
    sound?.checkOvertime(remaining, session.graceSeconds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const target = e.target as HTMLElement;
    const typing = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
    if (typing) return;

    if (e.key === " " || e.code === "Space") {
      e.preventDefault();
      onNext(idx);
    } else if (e.key.toLowerCase() === "p") {
      if (session.phase === "paused") onResume();
      else onPause();
    } else if (e.key.toLowerCase() === "f") {
      setExpanded((v) => !v);
    }
  }

  const showControls = size === "panel";

  return (
    <div
      ref={rootRef}
      className={styles.root}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="Standup timer, running"
    >
      {!shared && (
        <Text role="status">not shared — others cannot see this clock</Text>
      )}

      <div aria-live="polite" className={styles.sr}>
        {politeMessage}
      </div>
      <div aria-live="assertive" className={styles.sr}>
        {assertiveMessage}
      </div>

      <p className={styles.turnLabel}>
        Speaker {Math.min(idx + 1, session.queue.length)} of {session.queue.length}
      </p>
      <h1 className={styles.speaker}>{current?.displayName ?? "—"}</h1>

      <Countdown remainingSeconds={remaining} colour={colour} />

      <ProgressBar value={progress} thickness="large" aria-label="Time remaining" />

      <Text className={styles.total}>
        Meeting total: {formatDuration(total)} of a {formatDuration(budget)} budget
      </Text>

      {expanded && <Queue session={session} now={now} />}

      {showControls && (
        <>
          <div className={styles.controls}>
            <Button appearance="primary" onClick={() => onNext(idx)}>
              Next
            </Button>
            <Button onClick={session.phase === "paused" ? onResume : onPause}>
              {session.phase === "paused" ? "Resume" : "Pause"}
            </Button>
            <Button onClick={() => onExtend(30)}>+30s</Button>
            <Switch
              checked={soundEnabled}
              onChange={(_, data) => onToggleSound(data.checked)}
              label="Sound"
            />
            <Dialog open={endConfirmOpen} onOpenChange={(_, data) => setEndConfirmOpen(data.open)}>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">End</Button>
              </DialogTrigger>
              <DialogSurface>
                <DialogBody>
                  <DialogTitle>End the standup?</DialogTitle>
                  <DialogContent>This ends the standup for everyone.</DialogContent>
                  <DialogActions>
                    <DialogTrigger disableButtonEnhancement>
                      <Button appearance="secondary">Cancel</Button>
                    </DialogTrigger>
                    <Button
                      appearance="primary"
                      onClick={() => {
                        setEndConfirmOpen(false);
                        onEnd();
                      }}
                    >
                      End standup
                    </Button>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>
          </div>

          <AddPerson candidates={rosterCandidates} onAddManual={onAddManual} onAddInvited={onAddInvited} />
        </>
      )}
    </div>
  );
}
